import { useState } from 'react';
import { Link } from 'react-router-dom';

const CulturePage = () => {
  const [selectedValue, setSelectedValue] = useState(0);

  const coreValues = [
    {
      title: 'Innovation First',
      description: 'We embrace cutting-edge technologies and encourage creative problem-solving to deliver exceptional results for our clients.',
      icon: 'ri-lightbulb-line',
      details: 'Our team stays ahead of technology trends, experimenting with new tools and methodologies. We invest 20% of our time in research and development, ensuring we can offer the most advanced solutions to our clients.',
      image: 'https://public.readdy.ai/ai/img_res/7c69c8e44be51e8e0a727cf6e297e901.jpg'
    },
    {
      title: 'Client Success',
      description: 'Our clients\' success is our success. We go above and beyond to deliver exceptional results and build lasting partnerships.',
      icon: 'ri-customer-service-line',
      details: 'We measure our success by our clients\' achievements. Our dedicated account management ensures every project exceeds expectations, with 95% client retention rate and average project satisfaction score of 4.8/5.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20client%20meeting%20with%20IT%20consultants%2C%20successful%20business%20partnership%2C%20handshake%20agreement%20in%20modern%20office%2C%20client%20satisfaction&width=600&height=400&seq=client-success&orientation=landscape'
    },
    {
      title: 'Continuous Learning',
      description: 'We invest in our people and foster a culture of continuous learning and professional growth.',
      icon: 'ri-book-open-line',
      details: 'Each team member receives £5,000 annual learning budget, access to premium training platforms, conference attendance, and certification support. We host weekly tech talks and monthly innovation showcases.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20development%20training%20session%2C%20IT%20professionals%20learning%20new%20technologies%2C%20certification%20workshop%20in%20modern%20classroom%20setting&width=600&height=400&seq=learning-culture&orientation=landscape'
    },
    {
      title: 'Collaboration',
      description: 'We believe in the power of teamwork and diverse perspectives to drive innovation and deliver outstanding outcomes.',
      icon: 'ri-team-line',
      details: 'Our open office design encourages cross-team collaboration. We use agile methodologies, practice pair programming, and maintain transparent communication channels. Regular team retrospectives ensure continuous improvement.',
      image: 'https://readdy.ai/api/search-image?query=Diverse%20team%20of%20IT%20professionals%20collaborating%20in%20modern%20open%20office%2C%20agile%20teamwork%2C%20inclusive%20workplace%20with%20natural%20lighting&width=600&height=400&seq=collaboration-culture&orientation=landscape'
    }
  ];

  const workLifeBalance = [
    {
      title: 'Flexible Hours',
      description: 'Core hours 10am-3pm, with flexible start and finish times to accommodate personal schedules.',
      icon: 'ri-time-line'
    },
    {
      title: 'Hybrid Working',
      description: '3 days in office, 2 days remote as standard, with additional flexibility for personal circumstances.',
      icon: 'ri-home-office-line'
    },
    {
      title: 'Mental Health Support',
      description: 'Employee assistance program, mental health days, and wellbeing initiatives.',
      icon: 'ri-heart-pulse-line'
    },
    {
      title: 'Generous Leave',
      description: '25 days holiday plus bank holidays, with additional sabbatical options after 3 years.',
      icon: 'ri-calendar-check-line'
    }
  ];

  const teamEvents = [
    {
      title: 'Monthly Team Lunches',
      description: 'Regular team building over great food, celebrating achievements and strengthening relationships.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20team%20lunch%20in%20modern%20restaurant%2C%20colleagues%20celebrating%20success%2C%20team%20building%20social%20event&width=400&height=300&seq=team-lunch&orientation=landscape'
    },
    {
      title: 'Annual Company Retreat',
      description: 'Weekend away combining strategic planning, team activities, and relaxation.',
      image: 'https://readdy.ai/api/search-image?query=Corporate%20retreat%20team%20building%20activities%2C%20professional%20group%20outdoor%20activities%2C%20company%20offsite%20event%20in%20natural%20setting&width=400&height=300&seq=company-retreat&orientation=landscape'
    },
    {
      title: 'Innovation Hackathons',
      description: 'Quarterly 24-hour innovation challenges where teams create solutions for real client problems.',
      image: 'https://readdy.ai/api/search-image?query=Technology%20hackathon%20event%2C%20developers%20coding%20together%2C%20innovation%20competition%20in%20modern%20tech%20space&width=400&height=300&seq=hackathon&orientation=landscape'
    },
    {
      title: 'Tech Talks & Workshops',
      description: 'Weekly internal presentations and external speaker sessions on latest technology trends.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20tech%20presentation%20in%20modern%20conference%20room%2C%20speaker%20presenting%20to%20IT%20audience%2C%20technology%20workshop%20session&width=400&height=300&seq=tech-talks&orientation=landscape'
    }
  ];

  const testimonials = [
    {
      name: 'Sarah Mitchell',
      role: 'Senior Cloud Architect',
      tenure: '3 years',
      quote: 'The learning opportunities here are incredible. I\'ve gained 5 new certifications and led projects I never thought possible. The support from leadership is outstanding.',
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20confident%20female%20IT%20architect%2C%20modern%20business%20portrait%2C%20professional%20woman%20in%20tech&width=150&height=150&seq=sarah-testimonial&orientation=squarish'
    },
    {
      name: 'James Rodriguez',
      role: 'DevOps Engineer',
      tenure: '2 years',
      quote: 'The work-life balance is genuine, not just a buzzword. I can pick up my kids from school and still deliver excellent work. The team truly supports each other.',
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20hispanic%20male%20DevOps%20engineer%2C%20friendly%20business%20portrait%2C%20IT%20professional%20smiling&width=150&height=150&seq=james-testimonial&orientation=squarish'
    },
    {
      name: 'Priya Patel',
      role: 'Cybersecurity Consultant',
      tenure: '18 months',
      quote: 'Coming from a traditional corporate environment, the innovation culture here is refreshing. We\'re encouraged to try new approaches and learn from failures.',
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20indian%20female%20cybersecurity%20expert%2C%20confident%20business%20portrait%2C%20tech%20professional%20woman&width=150&height=150&seq=priya-testimonial&orientation=squarish'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link 
            to="/careers" 
            className="inline-flex items-center text-yellow-600 hover:text-yellow-700 font-medium cursor-pointer"
          >
            <i className="ri-arrow-left-line mr-2"></i>
            Back to Careers
          </Link>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-yellow-50 to-green-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Our <span className="text-yellow-500">Culture</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Discover what makes Yellow Atlas Consulting a great place to work. Our culture is built on innovation, collaboration, and genuine care for our people.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/careers"
                className="bg-yellow-500 text-white px-8 py-3 rounded-lg font-semibold hover:bg-yellow-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                View Open Positions
              </Link>
              <Link
                to="/contact"
                className="border-2 border-yellow-500 text-yellow-600 px-8 py-3 rounded-lg font-semibold hover:bg-yellow-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                Contact Our Team
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values - Interactive */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Core Values
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              These values guide our decisions, shape our culture, and define how we work together.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Value Selection */}
            <div className="space-y-4">
              {coreValues.map((value, index) => (
                <div
                  key={index}
                  onClick={() => setSelectedValue(index)}
                  className={`p-6 rounded-xl cursor-pointer transition-all ${
                    selectedValue === index
                      ? 'bg-gradient-to-r from-yellow-500 to-green-500 text-white shadow-lg'
                      : 'bg-white hover:bg-gray-50 shadow-sm border border-gray-200'
                  }`}
                >
                  <div className="flex items-center mb-3">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center mr-4 ${
                      selectedValue === index ? 'bg-white/20' : 'bg-gradient-to-r from-yellow-400 to-green-500'
                    }`}>
                      <i className={`${value.icon} text-xl ${
                        selectedValue === index ? 'text-white' : 'text-white'
                      }`}></i>
                    </div>
                    <h3 className="text-xl font-bold">{value.title}</h3>
                  </div>
                  <p className={selectedValue === index ? 'text-white/90' : 'text-gray-600'}>
                    {value.description}
                  </p>
                </div>
              ))}
            </div>

            {/* Value Details */}
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <img
                src={coreValues[selectedValue].image}
                alt={coreValues[selectedValue].title}
                className="w-full h-64 object-cover object-top rounded-lg mb-6"
              />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                {coreValues[selectedValue].title}
              </h3>
              <p className="text-gray-600 text-lg leading-relaxed">
                {coreValues[selectedValue].details}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Work-Life Balance */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Work-Life Balance
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We believe that happy, well-rested employees do their best work. Our policies support your personal life as much as your professional growth.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {workLifeBalance.map((item, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-yellow-400 to-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${item.icon} text-white text-2xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Events */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Team Events & Activities
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Building strong relationships through shared experiences, learning opportunities, and celebration of our achievements.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {teamEvents.map((event, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <img
                  src={event.image}
                  alt={event.title}
                  className="w-full h-48 object-cover object-top"
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{event.title}</h3>
                  <p className="text-gray-600">{event.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Employee Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Our Team Says
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Hear directly from our team members about their experience working at Yellow Atlas Consulting.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center mb-4">
                  <img
                    src={testimonial.avatar}
                    alt={testimonial.name}
                    className="w-16 h-16 rounded-full object-cover object-top mr-4"
                  />
                  <div>
                    <h4 className="font-bold text-gray-900">{testimonial.name}</h4>
                    <p className="text-gray-600 text-sm">{testimonial.role}</p>
                    <p className="text-yellow-600 text-sm font-medium">{testimonial.tenure} with us</p>
                  </div>
                </div>
                <p className="text-gray-600 italic">"{testimonial.quote}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Office Environment */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Our Manchester Office
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Our modern office in the heart of Manchester provides an inspiring environment for collaboration and innovation. With open spaces, quiet zones, and state-of-the-art technology, we've created a space that supports different working styles.
              </p>
              <div className="space-y-4">
                <div className="flex items-center">
                  <i className="ri-check-line text-green-500 mr-3"></i>
                  <span className="text-gray-700">Open collaborative workspaces</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-check-line text-green-500 mr-3"></i>
                  <span className="text-gray-700">Quiet focus areas and private meeting rooms</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-check-line text-green-500 mr-3"></i>
                  <span className="text-gray-700">Fully equipped kitchen and relaxation areas</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-check-line text-green-500 mr-3"></i>
                  <span className="text-gray-700">High-speed internet and latest technology</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-check-line text-green-500 mr-3"></i>
                  <span className="text-gray-700">Central Manchester location with excellent transport links</span>
                </div>
              </div>
            </div>
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20IT%20office%20space%20in%20Manchester%2C%20open%20plan%20workspace%20with%20collaborative%20areas%2C%20contemporary%20design%20with%20natural%20lighting%20and%20technology%20setup&width=600&height=400&seq=office-environment&orientation=landscape"
                alt="Office Environment"
                className="w-full h-96 object-cover object-top rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-yellow-500 to-green-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Join Our Team?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            If our culture resonates with you and you're passionate about technology and innovation, we'd love to hear from you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/careers"
              className="bg-white text-yellow-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              View Open Positions
            </Link>
            <Link
              to="/contact"
              className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-yellow-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get in Touch
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CulturePage;